import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mockDocuments } from '@/lib/demo-data/customer-documents';
import type { CustomerDocument, DocumentUpload } from '@/types/customer-document';

// Simulate API delay
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Fetch documents for a customer
const fetchCustomerDocuments = async (customerId: string): Promise<CustomerDocument[]> => {
  await delay(500);
  return mockDocuments[customerId] || [];
};

// Upload a document
const uploadDocument = async (
  customerId: string,
  upload: DocumentUpload,
  userId: string,
  userName: string,
): Promise<CustomerDocument> => {
  await delay(1000);

  const newDocument: CustomerDocument = {
    id: `doc-${Date.now()}`,
    customerId,
    fileName: upload.file.name,
    fileType: upload.file.type,
    fileSize: upload.file.size,
    fileUrl: '#', // In a real app, this would be the uploaded file URL
    uploadDate: new Date().toISOString(),
    uploadedBy: userId,
    uploadedByName: userName,
    description: upload.description,
    category: upload.category,
  };

  // Add to mock data
  if (!mockDocuments[customerId]) {
    mockDocuments[customerId] = [];
  }
  mockDocuments[customerId].unshift(newDocument);

  return newDocument;
};

// Delete a document
const deleteDocument = async (customerId: string, documentId: string): Promise<void> => {
  await delay(500);

  if (mockDocuments[customerId]) {
    mockDocuments[customerId] = mockDocuments[customerId].filter((doc) => doc.id !== documentId);
  }
};

// Hook to fetch customer documents
export function useCustomerDocuments(customerId: string | undefined) {
  return useQuery({
    queryKey: ['customerDocuments', customerId],
    queryFn: () => fetchCustomerDocuments(customerId!),
    enabled: !!customerId,
  });
}

// Hook to upload a document
export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customerId,
      upload,
      userId,
      userName,
    }: {
      customerId: string;
      upload: DocumentUpload;
      userId: string;
      userName: string;
    }) => uploadDocument(customerId, upload, userId, userName),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['customerDocuments', variables.customerId] });
    },
  });
}

// Hook to delete a document
export function useDeleteDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerId, documentId }: { customerId: string; documentId: string }) =>
      deleteDocument(customerId, documentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['customerDocuments', variables.customerId] });
    },
  });
}
